import { createContext, useContext } from 'react';
import type { Dispatch } from 'react';
import { Block, Chapter, Section } from '@/utils/bookStructure';

export type ShikakuOption = {
    id: string;
    name: string;
};

export type BookListItem = {
    id: string;
    title: string;
    edition?: string;
    isPublished: boolean;
};

export type BookStructureState = {
    loading: boolean;
    error: string | null;
    shikakuOptions: ShikakuOption[];
    books: BookListItem[];
    chapters: Chapter[];
    sections: Section[];
    blocks: Block[];
    selectedShikakuId: string;
    selectedBookId: string;
    selectedChapterId: string;
    selectedSectionId: string;
};

export type BookStructureAction =
    | { type: 'loading'; payload: boolean }
    | { type: 'error'; payload: string | null }
    | { type: 'shikakuOptions'; payload: ShikakuOption[] }
    | { type: 'books'; payload: BookListItem[] }
    | { type: 'chapters'; payload: Chapter[] }
    | { type: 'sections'; payload: Section[] }
    | { type: 'blocks'; payload: Block[] }
    | { type: 'selectedShikakuId'; payload: string }
    | { type: 'selectedBookId'; payload: string }
    | { type: 'selectedChapterId'; payload: string }
    | { type: 'selectedSectionId'; payload: string };

export const initialBookStructureState: BookStructureState = {
    loading: false,
    error: null,
    shikakuOptions: [],
    books: [],
    chapters: [],
    sections: [],
    blocks: [],
    selectedShikakuId: '',
    selectedBookId: '',
    selectedChapterId: '',
    selectedSectionId: '',
};

export const bookStructureReducer = (state: BookStructureState, action: BookStructureAction): BookStructureState => {
    switch (action.type) {
        case 'loading':
            return { ...state, loading: action.payload };
        case 'error':
            return { ...state, error: action.payload };
        case 'shikakuOptions':
            return { ...state, shikakuOptions: action.payload };
        case 'books':
            return { ...state, books: action.payload };
        case 'chapters':
            return { ...state, chapters: action.payload };
        case 'sections':
            return { ...state, sections: action.payload };
        case 'blocks':
            return { ...state, blocks: action.payload };
        case 'selectedShikakuId':
            return { ...state, selectedShikakuId: action.payload };
        case 'selectedBookId':
            return { ...state, selectedBookId: action.payload };
        case 'selectedChapterId':
            return { ...state, selectedChapterId: action.payload };
        case 'selectedSectionId':
            return { ...state, selectedSectionId: action.payload };
        default:
            return state;
    }
};

export type BookStructureContextValue = {
    state: BookStructureState;
    dispatch: Dispatch<BookStructureAction>;
};

export const BookStructureContext = createContext<BookStructureContextValue | null>(null);

export const useBookStructure = () => {
    const context = useContext(BookStructureContext);
    if (!context) {
        throw new Error('useBookStructure must be used within BookStructureContext');
    }
    return context;
};
